// AI assisted development
import type { Pipeline, PipelineStage } from '../types/devagent';

type PipelineStatusInput = Pick<Pipeline, 'status' | 'current_stage' | 'stages' | 'defect_investigation' | 'lifecycle_status' | 'error_message' | 'pr_status'>;

const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'deleted', 'abandoned']);
const HARD_TERMINAL_STATUSES = new Set(['cancelled', 'deleted', 'abandoned']);
const ACTIVE_STAGE_STATUSES = new Set(['running', 'in_progress']);
const DELIVERED_STAGES = new Set(['pr_merged', 'deployed_qa', 'deployed_prod']);
const MERGED_PR_STATUSES = new Set(['completed', 'merged', 'pr_merged']);
const APPROVAL_READY_STAGE_ALIASES: Record<string, string> = {
  spec_generated: 'spec',
  plan_generated: 'plan',
  tasks_generated: 'tasks',
  analyze_generated: 'analyze',
  tech_review_generated: 'plan',
};
const FAILED_STATUS_APPROVAL_READY_STAGES = new Set(['tech_review_generated']);
const PR_MONITORING_ELAPSED_REASONS = new Set([
  'maximum_monitor_cycles_reached',
  'maximum_elapsed_time_reached',
]);

function normalize(value?: string): string {
  return (value || '').toLowerCase();
}

export function isPrMonitoringElapsedReason(...values: unknown[]): boolean {
  return values.some((value) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return [...PR_MONITORING_ELAPSED_REASONS].some(reason => normalized.includes(reason));
  });
}

export function prMonitoringElapsedMessage(platform?: string | null): string {
  const normalizedPlatform = String(platform || '').trim().toLowerCase();
  const provider = normalizedPlatform === 'azure_devops' || normalizedPlatform === 'azure devops'
    ? 'Azure DevOps'
    : normalizedPlatform === 'github'
      ? 'GitHub'
      : normalizedPlatform === 'gitlab'
        ? 'GitLab'
        : 'Pull request';
  const subject = provider === 'Pull request' ? provider : `${provider} pull request`;
  return `${subject} monitoring time has elapsed. Resume PR Guardian to start another monitoring window.`;
}

function getStageTimestamp(stage: PipelineStage): number {
  return Math.max(
    Date.parse(String((stage as any).completed_at || '')) || 0,
    Date.parse(String((stage as any).updated_at || '')) || 0,
    Date.parse(String((stage as any).started_at || '')) || 0,
    Date.parse(String(stage.created_at || '')) || 0,
  );
}

function hasStageError(stage: PipelineStage): boolean {
  return Boolean((stage as any).error_message || (stage as any).error_type);
}

export function isArtifactStageAvailable(stage?: PipelineStage): boolean {
  if (!stage) {
    return false;
  }
  return normalize(stage.status) === 'completed' && !hasStageError(stage);
}

export function isArtifactStageStale(
  stage: PipelineStage | undefined,
  latestSpecStage: PipelineStage | undefined,
): boolean {
  if (!stage) {
    return false;
  }
  if (normalize((stage as any).error_type) === 'artifact_invalidated') {
    return true;
  }
  if (!latestSpecStage || normalize(latestSpecStage.status) !== 'completed') {
    return false;
  }
  return getStageTimestamp(latestSpecStage) > getStageTimestamp(stage);
}

function isStageActivelyRunning(stage: PipelineStage): boolean {
  return ACTIVE_STAGE_STATUSES.has(normalize(stage.status)) &&
    !(stage as any).completed_at &&
    !hasStageError(stage);
}

function isStageFailed(stage: PipelineStage): boolean {
  if (
    normalize(stage.status) === 'cancelled' &&
    normalize((stage as any).error_type) === 'artifact_invalidated'
  ) {
    return false;
  }
  return normalize(stage.status) === 'failed' || hasStageError(stage);
}

export function getLatestStagesByName(stages?: PipelineStage[]): Map<string, PipelineStage> {
  const latestStagesByName = new Map<string, PipelineStage>();

  for (const stage of stages || []) {
    const existing = latestStagesByName.get(stage.stage_name);
    if (!existing || getStageTimestamp(stage) >= getStageTimestamp(existing)) {
      latestStagesByName.set(stage.stage_name, stage);
    }
  }

  return latestStagesByName;
}

function generatedStageCompleted(currentStage: string, latestStages: Map<string, PipelineStage>): boolean {
  if (!currentStage.endsWith('_generated')) {
    return false;
  }
  const stageName = APPROVAL_READY_STAGE_ALIASES[currentStage] || currentStage.replace(/_generated$/, '');
  const stage = latestStages.get(stageName);
  if (!stage) {
    return currentStage === 'tech_review_generated';
  }
  return normalize(stage.status) === 'completed';
}

function generatedStageSupersedesFailures(
  currentStage: string,
  latestStages: Map<string, PipelineStage>,
): boolean {
  if (!currentStage.endsWith('_generated')) {
    return false;
  }
  const stageName = APPROVAL_READY_STAGE_ALIASES[currentStage] || currentStage.replace(/_generated$/, '');
  const generatedStage = latestStages.get(stageName);
  if (!generatedStage || normalize(generatedStage.status) !== 'completed') {
    return false;
  }
  const failureTimestamps = Array.from(latestStages.values())
    .filter(isStageFailed)
    .map(getStageTimestamp);
  if (failureTimestamps.length === 0) {
    return true;
  }
  return getStageTimestamp(generatedStage) > Math.max(...failureTimestamps);
}

export function isPipelineActivelyGenerating(pipeline: PipelineStatusInput): boolean {
  const currentStage = normalize(pipeline.current_stage);
  if (!currentStage.endsWith('_generating')) {
    return false;
  }
  const normalizedStatus = normalize(pipeline.status as string);
  return (
    normalizedStatus === 'in_progress' ||
    normalizedStatus === 'running' ||
    normalizedStatus === 'pending'
  );
}

function completedInvestigationWaitingForReview(pipeline: PipelineStatusInput): boolean {
  const investigation = pipeline.defect_investigation;
  const currentAttempt = investigation?.current_attempt;
  if (!currentAttempt || currentAttempt.status !== 'completed') {
    return false;
  }
  const hasRunningAttempt = (investigation.attempts || []).some((attempt) =>
    attempt.status === 'pending' || attempt.status === 'running'
  );
  return !hasRunningAttempt && (investigation.review_status || 'pending') === 'pending';
}

export function getEffectivePipelineStatus(pipeline: PipelineStatusInput): string {
  if (normalize(pipeline.lifecycle_status) === 'retired') {
    return 'retired';
  }

  const normalizedStatus = normalize(pipeline.status as string);
  const currentStage = normalize(pipeline.current_stage);
  const prStatus = normalize(pipeline.pr_status);
  const latestStages = getLatestStagesByName(pipeline.stages);
  const latestImplementationStatus = normalize(latestStages.get('implementation')?.status);
  const latestPrStatus = normalize(latestStages.get('pr')?.status);

  if (HARD_TERMINAL_STATUSES.has(normalizedStatus)) {
    return pipeline.status;
  }

  const anyStageRunning = Array.from(latestStages.values()).some(isStageActivelyRunning);
  const anyLatestStageFailed = Array.from(latestStages.values()).some(isStageFailed);
  const implementationCompleted =
    currentStage === 'implementation_completed' || latestImplementationStatus === 'completed';
  const prCreated =
    currentStage === 'pr_created' || (implementationCompleted && latestPrStatus === 'completed');

  if (currentStage === 'manual_implementation_pending') {
    return 'manual_implementation_pending';
  }

  if (MERGED_PR_STATUSES.has(prStatus)) {
    return 'pr_merged';
  }

  if (isPrMonitoringElapsedReason(pipeline.error_message)) {
    return 'pr_monitoring_elapsed';
  }

  if (normalizedStatus === 'completed' && !['defect_investigation', 'defect_investigation_review'].includes(currentStage)) {
    return pipeline.status;
  }

  if (currentStage === 'handoff_ready') {
    return 'handoff_ready';
  }

  if (normalizedStatus === 'failed') {
    if (currentStage === 'pr_created' && prCreated && latestPrStatus === 'completed') {
      return 'pr_created';
    }
    if (FAILED_STATUS_APPROVAL_READY_STAGES.has(currentStage) && generatedStageCompleted(currentStage, latestStages)) {
      return currentStage;
    }
    const hasFailureEvidence = Boolean(pipeline.error_message) || anyLatestStageFailed;
    if (DELIVERED_STAGES.has(currentStage) && !hasFailureEvidence) {
      return currentStage;
    }
    return 'failed';
  }

  if (currentStage === 'defect_investigation_review') {
    return 'defect_investigation_review';
  }

  if (currentStage === 'defect_investigation') {
    if (completedInvestigationWaitingForReview(pipeline)) {
      return 'defect_investigation_review';
    }
    return 'defect_investigation';
  }

  if (TERMINAL_STATUSES.has(normalizedStatus)) {
    return pipeline.status;
  }

  if (DELIVERED_STAGES.has(currentStage)) {
    return currentStage;
  }

  if (prCreated) {
    return 'pr_created';
  }

  if (currentStage === 'documents_failed') {
    return 'failed';
  }

  if (currentStage === 'documents_approval_pending' || currentStage === 'documents_generated') {
    return 'waiting_approval';
  }

  if (FAILED_STATUS_APPROVAL_READY_STAGES.has(currentStage) && generatedStageCompleted(currentStage, latestStages)) {
    return currentStage;
  }

  // Active generation should win over stale failed stage rows from a prior attempt.
  if (currentStage.endsWith('_generating')) {
    return 'in_progress';
  }

  if (normalizedStatus === 'in_progress' && anyLatestStageFailed && !anyStageRunning) {
    if (generatedStageSupersedesFailures(currentStage, latestStages)) {
      return currentStage;
    }
    return 'failed';
  }

  if (generatedStageCompleted(currentStage, latestStages)) {
    return currentStage;
  }

  if (anyStageRunning) {
    return 'in_progress';
  }

  if (currentStage.endsWith('_generated')) {
    return currentStage;
  }

  if (normalizedStatus === 'waiting_approval') {
    return 'waiting_approval';
  }

  if (implementationCompleted) {
    return 'in_progress';
  }

  return pipeline.status;
}

export function isPipelineEffectivelyFailed(pipeline: PipelineStatusInput): boolean {
  return normalize(getEffectivePipelineStatus(pipeline)) === 'failed';
}

export function isPipelineEffectivelyComplete(pipeline: PipelineStatusInput): boolean {
  const effectiveStatus = normalize(getEffectivePipelineStatus(pipeline));
  return TERMINAL_STATUSES.has(effectiveStatus) || DELIVERED_STAGES.has(effectiveStatus);
}
